/* eslint-disable jsdoc/require-jsdoc */
/**
 * Function to convert string to Camelcase.
 * @param str - String to convert.
 * @returns - COnverted string.
 */
import moment from 'moment';
import { ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import 'moment-timezone';
import {
  AlertRule,
  DashboardDataFilter,
  DeviceAttributes,
  DeviceSummaryAttribute,
  SummaryMeta,
  DeviceType,
  ExtendedDeviceAttributes,
  ParameterAttribute,
  ParametersType,
  ParticleData,
  PriorityList,
  TimeSpan,
  UserConfig
} from '../constants/telosair-interfaces';
import { getOverViewChart } from '../services/dashboard-service';

/**
 * Function to convert string to camel case.
 * @param str - String to convert.
 * @returns - Converted string.
 */
export function convertToCamelCase(str: string): string {
  return str.replace('-', (word, index) => {
    return index === 0 ? word.toUpperCase() : word.toLowerCase();
  });
}

/**
 * Get displayable value based on particleData key:value.
 * @param parameter - Parameter of the value.
 * @param num - The number to be converted.
 * @returns  Rounded value for the given number or '-'.
 */
export const getFixedNumberForGraph = (parameter: ParametersType, num?: number | null): number => {
  if (
    num !== null &&
    num !== undefined &&
    num !== -9999 &&
    num !== Infinity &&
    num !== -Infinity &&
    String(num) !== 'Infinity' &&
    String(num) !== '-Infinity'
  ) {
    return num;
  }
  return 0;
};

/**
 * Get displayable value based on particleData key:value.
 * @param parameter - Parameter of the value.
 * @param num - The number to be converted.
 * @returns  Rounded value for the given number or '-'.
 */
export const getDisplayableValue = (parameter: string, num?: number | null): string => {
  if (
    num !== null &&
    num !== undefined &&
    num !== -9999 &&
    num !== Infinity &&
    num !== -Infinity &&
    String(num) !== 'Infinity' &&
    String(num) !== '-Infinity' &&
    String(num) !== 'NaN'
  ) {
    if (
      parameter === 'pm100_m' ||
      parameter === 'pm25_m' ||
      parameter === 'temp' ||
      parameter === 'co' ||
      parameter === 'no2' ||
      parameter === 'o3'
    ) {
      return num.toFixed(1);
    }
    return Math.round(num).toString();
  }
  return '-';
};

/**
 * Getclasses function for getting the class for the component.
 * @param priority - Priority of the parameter.
 * @param type - Type of style.
 * @returns - String based on the priority of the parameter.
 */
export const getCSSClassBasedOnPriority = (
  priority: PriorityList,
  type: 'text' | 'background'
): string => {
  let classes = '';
  if (priority === PriorityList.high) {
    classes = type === 'background' ? 'bg-error-500' : 'text-error-500';
  } else if (priority === PriorityList.medium) {
    classes = type === 'background' ? 'bg-warning-500' : 'text-warning-500';
  } else if (priority === PriorityList.low) {
    classes = type === 'background' ? 'bg-success-500' : 'text-success-500';
  } else if (priority === PriorityList.outOfRange) {
    classes = type === 'background' ? 'bg-error-800' : 'text-error-800';
  } else {
    classes = type === 'background' ? 'bg-primary-500' : 'text-gray-500';
  }
  return classes;
};

/**
 * Function to get base64 data of a file.
 * @param file - Selected file.
 * @returns - Base64 data.
 */
export async function getBase64(file: File): Promise<string | ArrayBuffer | null> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    /**
     * File reader on load function.
     */
    reader.onload = () => {
      resolve(reader.result);
    };
    reader.onerror = reject;
  });
}

/**
 * Function to get value by the given parameter.
 * @param key - Parameter key.
 * @param particleData - Data of the device.
 * @returns - Value of the parameter.
 */
export const getValueByParameter = (
  key: ParametersType,
  particleData: ParticleData
): number | null => {
  return particleData && (particleData as any)[key];
};

/**
 * Convert an array of objects to a single object with specified keys.
 * @param array - The array of objects to convert.
 * @param keyName - The key name in each object to use as the key in the resulting object.
 * @param valueName - The key name in each object to use as the value in the resulting object.
 * @returns The resulting object.
 */
export function arrayToObject<T>(
  array: T[],
  keyName: keyof T,
  valueName: keyof T
): Record<any, string> {
  return array?.reduce((obj: any, item) => {
    // eslint-disable-next-line no-param-reassign
    obj[item[keyName]] = item[valueName];
    return obj;
  }, {});
}

/**
 * Function to check if it's a timestamp.
 * @param value - Timestamp value.
 * @returns - Boolean value.
 */
function isTimestamp(value: number): boolean {
  // Simple check if the value is a Unix timestamp in milliseconds
  return value > 1000000000000;
}

/**
 * Function to standardize data for graphs.
 * @param data - Data.
 * @returns - Updated data.
 */
function standardizeData(data: number[][]): number[][] {
  const standardized = data.map((item) => {
    // Check if the first element is a timestamp
    if (isTimestamp(item[0])) {
      // Assuming the first element is a timestamp, return as is
      return item;
    }
    const standardizedItem = [item[1], item[0]];
    return standardizedItem;
  });
  return standardized;
}

/**
 * Function to change 0 value to null in an array with 2 numbers.
 * @param data - Data of the graph.
 * @returns - Changed data.
 */
export const changeZeroValuesToNull = (data: number[][]): any => {
  const updatedStndardizedData = standardizeData(data);
  const value: any = updatedStndardizedData.map((graphValue: any) => {
    if (graphValue[1] === 0 || graphValue[1] === -9999) {
      return [graphValue[0], null];
    }
    return graphValue;
  });
  return value;
};

/**
 * Function to change 0 value to null in an array with 2 numbers.
 * @param data - Data of the graph.
 * @returns - Changed data.
 */
export const changeZeroValuesToNullForNumber = (data: number): any => {
  if (data === 0 || data === -9999) {
    return null;
  }
  return data;
};

/**
 * Function to get number of grids to display with respect to array size.
 * @param length - Length of the array.
 * @returns Tailwind grid class.
 */
export const getGridCount = (length: number): string => {
  return `grid-cols-${length}`;
};

/**
 * Function to get parameter by name.
 * @param name - Name of the parameter.
 * @param parameters - List of parameters.
 * @returns Parameter.
 */
export const getParameterByName = (
  name: string,
  parameters: ParameterAttribute[]
): ParameterAttribute | null => {
  const parameter = parameters.filter((item) => {
    if (item.displayName === name || item.parameterName === name || item.shortName === name) {
      return true;
    }
    return false;
  });
  if (parameter.length > 0) {
    return parameter[0];
  }
  return null;
};

export const generateConfigKey = {
  // eslint-disable-next-line jsdoc/require-jsdoc
  min: (paramater: ParametersType) => {
    return `${paramater}_threshold_min`;
  },
  // eslint-disable-next-line jsdoc/require-jsdoc
  max: (paramater: ParametersType) => {
    return `${paramater}_threshold_max`;
  }
};

/**
 * Get data from get-chart-data api based on the device client, facility, room and sensor.
 * @param deviceSerialNumber - Serial number of the device.
 * @param timeSpanDate - Data object.
 * @param timeSpanDate.startDate - Start date of the data.
 * @param timeSpanDate.endDate - End date of the data.
 * @returns - PArticled data for a device.
 */
export const getDeviceChartData = async (
  deviceSerialNumber: number,
  timeSpanDate: { startDate: number; endDate: number }
  // eslint-disable-next-line consistent-return
): Promise<any> => {
  try {
    const config = {
      clients: [],
      facility: [],
      zones: [],
      sensors: [],
      startDate: timeSpanDate.startDate,
      end: timeSpanDate.endDate,
      rooms: [],
      sensorNumber: [deviceSerialNumber]
    };
    const response = await getOverViewChart(config);
    if (response.data?.timeChart) {
      return response.data?.timeChart;
    }
    return null;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
  }
};

/**
 * Function to validate password.
 * @param password - Entered password.
 * @returns - Boolean.
 */
export const validatePassword = (password: string): { isValid: boolean; message: string } => {
  const re = {
    capital: /(?=.*[A-Z])/,
    length: /(?=.{8,})/,
    specialChar: /[ -\\/:-@\\[-\\`{-~]/,
    digit: /(?=.*[0-9])/
  };

  const validations = {
    capital: re.capital.test(password),
    length: re.length.test(password),
    specialChar: re.specialChar.test(password),
    digit: re.digit.test(password)
  };

  const messages = [];
  if (!validations.capital) messages.push('at least one uppercase letter');
  if (!validations.length) messages.push('minimum 8 characters');
  if (!validations.specialChar) messages.push('at least one special character');
  if (!validations.digit) messages.push('at least one digit');

  return {
    isValid:
      validations.capital && validations.length && validations.specialChar && validations.digit,
    message:
      messages.length === 0 ? 'Password is valid.' : `Password must contain ${messages.join(', ')}.`
  };
};

/**
 * Function to get x axis labels.
 * @param timestamp - Timestamp of the axis label.
 * @param userConfigData - Userconfig.
 * @param timeSpan - Timespan.
 * @returns - XAxis label.
 */
export const getFormattedXAxisLabel = (
  timestamp: number,
  userConfigData: UserConfig,
  timeSpan?: TimeSpan
): string => {
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (
    !timeSpan ||
    timeSpan === TimeSpan.Week1 ||
    timeSpan === TimeSpan.Month1 ||
    timeSpan === TimeSpan.Custom
  ) {
    return moment(timestamp).tz(timeZone).format('Do MMM');
  }
  return moment(timestamp).tz(timeZone).format('HH:mm');
};

/**
 * Function to get x axis labels.
 * @param timestamp - Timestamp of the axis label.
 * @param userConfigData - Userconfig.
 * @returns - XAxis label.
 */
export const getFormattedTooltipLabel = (timestamp: number, userConfigData: UserConfig): string => {
  const timeZone = userConfigData?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return moment(timestamp).tz(timeZone).format('hh:mm a, Do MMM yyy');
};

/**
 * Function to get background by Alert priority.
 * @param rule - ALertRule.
 * @returns - Background color.
 */
export const getBgClassNameByAlert = (rule: AlertRule[]): string => {
  if (rule.length > 0) {
    const selectedRule = rule[0];
    if (selectedRule.priority?.toLowerCase() === PriorityList.critical) {
      return 'bg-error-600';
    }
    if (selectedRule.priority?.toLowerCase() === PriorityList.high) {
      return 'bg-error-400';
    }
    if (selectedRule.priority?.toLowerCase() === PriorityList.medium) {
      return 'bg-warning-500';
    }
    if (selectedRule.priority?.toLowerCase() === PriorityList.low) {
      return 'bg-success-500';
    }
  }
  return 'bg-gray-400';
};

/**
 * Sorts the parent array based on the values at the specified index within each sub-array.
 * @param parentArray - The 2D array to be sorted.
 * @param index - The index of the inner arrays to sort by.
 * @param order - The order of sorting: 'asc' for ascending, 'dsc' for descending.
 * @returns - The sorted 2D array.
 */
export function sortParentArrayBySubArrayIndex(
  parentArray: Array<Array<number | string | null>>,
  index: number,
  order: 'asc' | 'dsc' = 'asc'
): any[] {
  return parentArray.sort((a, b) => {
    const valA = a[index];
    const valB = b[index];

    if (valA === null && valB === null) return 0;
    if (valA === null) return 1;
    if (valB === null) return -1;

    if (typeof valA === 'string' && typeof valB === 'string') {
      // For string comparison
      const comparison = valA.localeCompare(valB);
      return order === 'asc' ? comparison : -comparison;
    }

    if (typeof valA === 'number' && typeof valB === 'number') {
      // For numeric comparison
      const comparison = valA - valB;
      return order === 'asc' ? comparison : -comparison;
    }

    // Handle mixed types (number vs string)
    return typeof valA === 'number' ? -1 : 1;
  });
}

/**
 * Generates device coordinates based on the number of devices, rows, and the dimensions of the div.
 * Adds spacing (margin) around the edges of the div.
 * Ensures that when the number of rows/columns is small, devices don't get too much extra space.
 * @param devices - Array of devices to plot.
 * @param rows - Number of rows to divide the div into.
 * @param width - The width of the div in pixels.
 * @param height - The height of the div in pixels.
 * @param margin - The spacing (in pixels) from the edges of the div.
 * @returns - An object mapping device serial numbers to their normalized x and y coordinates.
 */
export const generateDeviceCoordinates = (
  devices: ExtendedDeviceAttributes[],
  rows: number,
  width: number,
  height: number,
  margin: number = 10 // Default margin from the edges in pixels
): Record<string, { x: number; y: number }> => {
  const coordinates: Record<string, { x: number; y: number }> = {};

  // Calculate the number of columns based on the number of devices and rows
  const columns = Math.ceil(devices.length / rows);

  // Calculate the effective width and height for plotting within the margins
  const effectiveWidth = width - 2 * margin;
  const effectiveHeight = height - 2 * margin;

  // Calculate the width and height of each grid cell
  const cellWidth = effectiveWidth / Math.max(columns, 1); // Ensure there is at least one column
  const cellHeight = effectiveHeight / Math.max(rows, 1); // Ensure there is at least one row

  // Loop through each device and assign its coordinates based on its position in the grid
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < devices.length; i++) {
    const row = Math.floor(i / columns); // Determine the row based on the device index
    const col = i % columns; // Determine the column based on the device index

    // If there are very few rows/columns, center devices within the available space
    const x = col * cellWidth + cellWidth / 2 + margin; // x coordinate, with margin
    const y = row * cellHeight + cellHeight / 2 + margin; // y coordinate, with margin

    // Normalize x and y to be percentages relative to the div's full width and height
    coordinates[devices[i].id] = {
      x: x / width,
      y: y / height
    };
  }

  return coordinates;
};

type WeeklyData = {
  data: number[][];
  heatMapResponse: null;
  xaxis: string[];
};

/**
 * Function to sort weekly data.
 * @param data - Weekly data.
 * @returns - Sorted weekly data.
 */
export function sortWeeklyData(data: WeeklyData): WeeklyData {
  // Define the correct order of weekdays
  const weekdayOrder = [
    'SUNDAY',
    'MONDAY',
    'TUESDAY',
    'WEDNESDAY',
    'THURSDAY',
    'FRIDAY',
    'SATURDAY'
  ];

  // Create array of objects with data and corresponding day
  const paired = data.xaxis.map((day, index) => ({
    day,
    values: data.data[index]
  }));

  // Sort based on weekday order
  paired.sort((a, b) => {
    return weekdayOrder.indexOf(a.day) - weekdayOrder.indexOf(b.day);
  });

  // Reconstruct the sorted data
  return {
    data: paired.map((item) => item.values),
    heatMapResponse: null,
    xaxis: paired.map((item) => item.day)
  };
}

// eslint-disable-next-line jsdoc/require-jsdoc
export function generateBooleanArray(n: number): boolean[] {
  const result = Array(n).fill(false);
  const maxTrueCount = Math.min(10, n);
  const interval = Math.floor(n / maxTrueCount);
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < maxTrueCount; i++) {
    result[i * interval] = true;
  }

  return result;
}

/**
 * GetSize for Device Icon returns the size of the device (circle) Icon on the bases of the size.
 * @param priority - Priority of the device.
 * @returns - Classes.
 */
export const getColorForMap = (priority: PriorityList): string => {
  if (priority === PriorityList.high) {
    return 'bg-error-500/20';
  }
  if (priority === PriorityList.medium) {
    return 'bg-warning-500/20';
  }
  if (priority === PriorityList.low) {
    return 'bg-success-500/20';
  }
  return '';
};

/**
 * A function for finding the right class for the device Icon based on device Priority.
 * @param priority - Priority of the device.
 * @param hasBorder - Condition to show border or not.
 * @returns - Classes of the circle Icon.
 */
export const classesBasedOnPriority = (priority: string, hasBorder?: boolean): string => {
  let classes = '';
  if (priority === 'high') {
    // TODO set position here
    classes = hasBorder ? ' text-error-500 group-hover:border-2 border-error-300' : 'bg-error-500';
  } else if (priority === 'medium') {
    classes = hasBorder
      ? 'text-warning-500 group-hover:border-2 border-warning-300'
      : 'text-warning-500';
  } else if (priority === 'low') {
    classes = hasBorder
      ? 'text-success-500 group-hover:border-2 border-success-300'
      : 'text-success-500';
  } else {
    classes = hasBorder
      ? 'text-primary-500 group-hover:border-2 border-primary-300'
      : 'text-primary-500';
  }
  return classes;
};

export function deviceFilter(
  dataFilter: DashboardDataFilter | null,
  groupDataSummary: Record<string, DeviceSummaryAttribute>,
  deviceDataSummary: Record<string, DeviceSummaryAttribute>,
  dataSummary: ParticleData,
  devices: DeviceAttributes[],
  groupTypes: Record<string, DeviceType>,
  dataSummaryMeta?: SummaryMeta
): {
  data: ParticleData;
  type: DeviceType;
  /** The values are a last known reading, not data from the selected window. */
  stale: boolean;
  /** When that last known reading was taken, if the API said. */
  asOf: number | null;
} {
  let data: ParticleData | null = null;
  let type: DeviceType = DeviceType.MIXED;
  let stale = false;
  let asOf: number | null = null;

  if (dataFilter?.type === 'group') {
    const summary = groupDataSummary[dataFilter.id];
    data = summary?.parameters;
    stale = summary?.stale === true;
    asOf = stale ? summary?.timeStamp ?? null : null;
    type = groupTypes[dataFilter?.id?.toString() || ''] as DeviceType;
  } else if (dataFilter?.type === 'device') {
    const summary = deviceDataSummary[dataFilter.id];
    data = summary?.parameters;
    stale = summary?.stale === true;
    asOf = stale ? summary?.timeStamp ?? null : null;
    type =
      devices.find((device) => device.serialNumber === dataFilter.id)?.deviceType ||
      DeviceType.MIXED;
  } else {
    data = dataSummary;
    stale = dataSummaryMeta?.stale === true;
    asOf = stale ? dataSummaryMeta?.timeStamp ?? null : null;
    type = DeviceType.MIXED;
  }

  return { data, type, stale, asOf };
}

export function deviceFilterWithDeviceType(
  dataFilter: DashboardDataFilter | null,
  devices: DeviceAttributes[],
  groupTypes: Record<string, DeviceType>
): DeviceType {
  if (dataFilter?.type === 'group') {
    return groupTypes[dataFilter?.id?.toString() || ''] as DeviceType;
  }
  if (dataFilter?.type === 'device') {
    return devices.find((device) => device.id === dataFilter.id)?.deviceType || DeviceType.MIXED;
  }
  return DeviceType.MIXED;
}

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function getParameterName(
  parameter: ParameterAttribute,
  deviceType: DeviceType
): ParameterAttribute {
  if (parameter.parameterName === 'aqi' && deviceType === DeviceType.INDOOR) {
    return {
      ...parameter,
      displayName: 'Indoor AQI',
      shortName: 'IAQI'
    };
  }
  return parameter;
}
